import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChevronDown, ChevronUp, MessageSquarePlus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/siteData";
import NotFound from "@/pages/NotFound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// The public-facing brand domain (embedded source stays internal/hidden).
const SCHEDULE_URL = "/#demo";

// Adds noindex/nofollow/noarchive robots meta while this page is mounted.
const useNoIndex = () => {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
};

export default function PublicDemo() {
  const { slug } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ok | notfound
  const [demo, setDemo] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useNoIndex();

  useEffect(() => {
    let active = true;
    setStatus("loading");
    axios
      .get(`${API}/demos/public/${slug}`)
      .then((res) => {
        if (!active) return;
        setDemo(res.data);
        setStatus("ok");
      })
      .catch(() => {
        if (!active) return;
        setStatus("notfound");
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="grid h-screen w-screen place-items-center bg-navy-deep">
        <div className="flex flex-col items-center gap-3 text-navy-muted">
          <img src={`${process.env.PUBLIC_URL || ""}/anchor-logo.png`} alt="" className="h-12 w-12 animate-pulse-glow object-contain" />
          <span className="text-sm">Loading demo…</span>
        </div>
      </div>
    );
  }

  if (status === "notfound") {
    return <NotFound />;
  }

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-navy-deep">
      {/* Compact demo toolbar */}
      <header
        className="z-10 shrink-0 border-b border-white/10 bg-navy shadow-card"
        aria-label="Anchor Web Systems demo toolbar"
      >
        {collapsed ? (
          <div className="flex items-center justify-between px-4 py-1.5">
            <span className="flex items-center gap-2 text-xs font-medium text-navy-muted">
              <img src={`${process.env.PUBLIC_URL || ""}/anchor-logo.png`} alt="Anchor Web Systems" className="h-5 w-5 object-contain" />
              Custom Website Demo by Anchor Web Systems
            </span>
            <Button
              variant="hero"
              size="sm"
              onClick={() => setCollapsed(false)}
              aria-expanded={false}
              aria-controls="demo-toolbar-body"
              aria-label="Expand demo toolbar"
            >
              <ChevronDown className="h-4 w-4" /> Show bar
            </Button>
          </div>
        ) : (
          <div
            id="demo-toolbar-body"
            className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-center gap-3">
              <img
                src={`${process.env.PUBLIC_URL || ""}/anchor-logo.png`}
                alt="Anchor Web Systems"
                className="h-8 w-8 shrink-0 object-contain"
              />
              <div className="min-w-0 leading-tight">
                <p className="truncate text-[11px] font-medium uppercase tracking-wider text-techblue-glow">
                  Custom Website Demo by Anchor Web Systems
                </p>
                <p className="truncate font-display text-sm font-bold text-navy-foreground">
                  {demo.business_name}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="silver" size="sm">
                <a href={SCHEDULE_URL} target="_blank" rel="noopener noreferrer">
                  <MessageSquarePlus className="h-4 w-4" /> Request Changes
                </a>
              </Button>
              <Button asChild variant="cta" size="sm">
                <a href={SCHEDULE_URL} target="_blank" rel="noopener noreferrer">
                  <Sparkles className="h-4 w-4" /> Get This Website
                </a>
              </Button>
              <Button
                variant="hero"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCollapsed(true)}
                aria-expanded={true}
                aria-controls="demo-toolbar-body"
                aria-label="Collapse demo toolbar"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Full-width responsive embed (source URL kept internal) */}
      <iframe
        src={demo.external_url}
        title={`${demo.business_name} — website demo`}
        className="min-h-0 w-full flex-1 border-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation-by-user-activation"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}
